import type { Geometry } from 'geojson';
import rootStore from '~/store';
import { saveExportDownload } from './fileDownload';

// Label helpers live in the store-free ./labels module (unit-testable); re-exported here to
// keep this file the one import surface for downloads code.
export { CUSTOM_DATA_LAYER_FALLBACK_LABEL, type GetLayerLabelOptions, getLayerLabel } from './labels';

export interface ProjectDownload {
  createdAt: string;
  updateAt: string;
  id: string;
  _id: string;
  user: {
    _id: string;
    name?: string;
    email: string;
  };
  projectId: string;
  organizationId: string;
  geometry: Geometry;
  downloadArea: Geometry | null;
  areaInAcres: number | null;
  imagery: boolean;
  notes: string;
  serverJobId?: string;
  downloadType?: string;
  jobStatus?: 'esriJobSucceeded' | 'esriJobExecuting' | 'esriJobSubmitted' | 'esriJobFailed';
  gpUrl?: string;
  layers?: string[];
  /** Whether this row produced a file of its own. The URL is signed per click, not per row. */
  hasFile?: boolean;
  projection?: {
    title: string;
    value: string;
  };
  // Groups the rows from one export submission — one per requested format. Null for rows created
  // before submissions existed, and for legacy ESRI downloads.
  submissionId?: string | null;
  // The org's custom FME template for this extract. Null is CivilGrid Default; absent on legacy ESRI rows.
  extractTemplateId?: string | null;
  // Whether the submission produced the archive every format was extracted from. False on legacy
  // ESRI rows, which have only a file of their own.
  hasCombinedArchive?: boolean;
  // Set while an admin-actionable escalation is open for this row's submission (e.g. a restricted
  // request awaiting review). Null when no escalation exists.
  escalationStatus?: 'pending' | 'completed' | 'archived' | null;
}

/** Downloads one format's own file, for the per-format history table that renders when multi-format is off. */
export const downloadFile = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, download: ProjectDownload) => {
  e.stopPropagation();

  return saveExportDownload(
    { projectId: download.projectId, downloadId: download.id, target: 'file' },
    {
      projectId: download.projectId,
      organizationId: download.organizationId || rootStore.currentOrganizationId || undefined,
      fileId: download._id,
      filetype: download.downloadType,
      source: 'downloads_tab',
    },
  );
};

export const statusPillStyles = {
  downloadBg: 'bg-brand',
  downloadText: 'text-white',
  pendingBg: 'bg-slate-500',
  pendingText: 'text-slate-100',
  failedBg: 'bg-red-100',
  failedText: 'text-red-600',
  noDataBg: 'bg-slate-100',
  noDataText: 'text-slate-600',
};
